<template>
  <div>
    <!-- Profession-City Page -->
    <template v-if="profession && city">
      <JoszakiPage inner-class="flex flex-col gap-4">
        <BreadCrumbs :links="getBreadCrumbs" />
        <!-- Header -->
        <div ref="header">
          <template v-if="abHierarchyHeader">
            <HierarchyHeader
              class="md:hidden"
              :profession="profession"
              :city="city"
              @requestTender="
                onRequestTender({ source: 'hierarchyHeader', text: $event })
              "
            />
            <div class="hidden md:block">
              <JoszakiHeading class="text-center mb-4">
                {{
                  $t("personList.lookingFor", {
                    city: city.name,
                    profession: $helpers.capitalize(profession.theperson),
                  })
                }}
              </JoszakiHeading>

              <div class="flex flex-col md:flex-row gap-3 w-full items-stretch">
                <div class="w-full grid gap-4">
                  <QuickOffer
                    class="flex-shrink flex-grow-[2]"
                    :placeholder="quickOfferPlaceholder"
                    @requestTender="
                      onRequestTender({ source: 'quickOffer', text: $event })
                    "
                  />
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <JoszakiHeading class="text-center mb-4">
              {{
                $t("personList.lookingFor", {
                  city: city.name,
                  profession: $helpers.capitalize(profession.theperson),
                })
              }}
            </JoszakiHeading>

            <div class="flex flex-col md:flex-row gap-3 w-full items-stretch">
              <div class="w-full grid gap-4">
                <QuickOffer
                  class="flex-shrink flex-grow-[2]"
                  :placeholder="quickOfferPlaceholder"
                  @requestTender="
                    onRequestTender({ source: 'quickOffer', text: $event })
                  "
                />
              </div>
            </div>
          </template>
        </div>

        <div class="listpage-info">
          <h2 class="font-700 text-primary text-2xl text-center">
            {{
              $t("personList.findPerson", {
                professionObjective: profession.objective,
              })
            }}
          </h2>
        </div>

        <div class="listpage-list-wrapper">
          <NotFoundPersonList
            v-if="!found"
            class="column is-full"
            :profession-name="professionName.toLowerCase()"
          />
          <b-loading
            :active="loading && personList.length < 1"
            :is-full-page="false"
          />
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <CardPerson
              v-for="person in personList"
              :key="person.id"
              class="h-full"
              :highlighted-city="city"
              :highlighted-profession="profession"
              :person="person"
              show-active-indicator
              @select="
                onClickPersonCard(person, personList.indexOf(person) + 1)
              "
              @callProfessional="onCallProfessional(person)"
              @requestTender="onRequestTender({ person, source: 'personCard' })"
            />
          </div>
        </div>

        <!-- Load more -->
        <PaginationComponent
          v-if="paginationInfo && paginationInfo.last !== 1"
          :pagination-info="paginationInfo"
        />

        <section class="block">
          <QuestionInsetBox
            v-if="questions && questions.length"
            :questions="questions"
            :action="{
              href: localePath({
                name: 'professionQuestions',
                params: {
                  profession: profession.seoName,
                },
              }),
            }"
          >
            <i18n path="personList.questionInsetBox.title">
              <template #profession>
                <br />
                <strong class="dynamic-text">{{ profession.name }}</strong>
              </template>
            </i18n>
          </QuestionInsetBox>
          <!-- Uncomment once question submission is open for users. -->
          <!-- <QuestionForwardBox v-else> -->
          <QuestionForwardBox v-if="false">
            <template slot="title">
              <i18n path="personList.questionForwardBox.title">
                <template #profession>
                  <strong class="dynamic-text">{{ profession.name }}</strong>
                </template>
              </i18n>
            </template>
          </QuestionForwardBox>
        </section>

        <OfferBox
          class="max-lg:hidden"
          :placeholder="$t('quickOffer.placeholder')"
          @requestTender="onRequestTender({ source: 'offerBox', text: $event })"
        />
        <BlockTender
          class="lg:hidden"
          text-input
          @requestTender="
            onRequestTender({ source: 'banner-block', text: $event })
          "
        />

        <div
          v-if="blocks.length > 0"
          class="flex flex-col-reverse lg:grid lg:grid-cols-[3fr_1fr] gap-3"
        >
          <div>
            <div v-for="block in blocks" :key="block.id">
              <section v-if="block.type === 'markdownArticleBlock'">
                <JoszakiHeading
                  :id="$helpers.stringToSlug(block.value.title)"
                  :level="2"
                  class="mb-2"
                >
                  {{ block.value.title }}
                </JoszakiHeading>
                <div
                  v-dangerouslySetInnerHTML="$md.render(block.value.markdown)"
                  class="markdown"
                />
              </section>
            </div>
          </div>
          <div>
            <TableOfContents
              class="lg:sticky lg:top-3"
              :articles="tableOfContentsData"
            ></TableOfContents>
          </div>
        </div>

        <!-- People rating summary -->
        <!--
        <div v-if="found" class="block text-center rating-summary">
          <PeopleRating
            :city-name="city.name"
            :profession="profession"
            :total-score="totals.averageScore / 2"
            :total-score-count="totals.totalScoreCount"
          />
        </div>
        -->

        <JoszakiCard v-if="extraText">
          <MarkdownTextBlock :text="extraText" />
        </JoszakiCard>
      </JoszakiPage>
      <ProfessionTaskLinks
        :popular-profession-tasks="popularProfessionTasks"
        :profession="profession"
      />
    </template>

    <!-- Profession-County Page -->
    <template v-else-if="profession && county">
      <JoszakiPage>
        <JoszakiHeading>
          {{
            $t("listPage.county.fromCounty", {
              professionPlural: profession.plural,
              countyName: county.name,
            })
          }}
        </JoszakiHeading>
        <QuickOffer
          :placeholder="quickOfferPlaceholder"
          class="mb-8"
          @requestTender="
            onRequestTender({ source: 'quickOffer', text: $event })
          "
        />
        <CityCountLinks
          v-if="cityPersonCounts?.length"
          :cities="cityPersonCounts"
          :profession="profession"
        />
        <div v-else class="content">
          {{ $t("listPage.county.noneFound") }}
        </div>
        <JoszakiButton
          type="info"
          :to="
            localePath({
              name: 'professionPage',
              params: { profession: profession.seoName },
            })
          "
        >
          {{
            $t("listPage.county.checkRest", {
              professionObjective: profession.objective,
            })
          }}
        </JoszakiButton>
      </JoszakiPage>
    </template>
    <NotFound v-else @requestTender="onRequestTender({ source: 'notFound' })" />
  </div>
</template>

<router>
{
name: 'professionals'
}
</router>

<script>
import PROFESSION_BLOCKS from "./professionBlocks.graphql";
import NunitoRegular from "~/assets/fonts/nunito-regular.woff2";
import NunitoSemibold from "~/assets/fonts/nunito-semibold.woff2";
import NunitoBold from "~/assets/fonts/nunito-bold.woff2";
import NunitoExtrabold from "~/assets/fonts/nunito-extrabold.woff2";

import PROFESSION_EXTRA_TEXTS from "@/data/professionExtraTexts.json";

import QuestionInsetBox from "~/components/QuestionInsetBox.vue";
import QuestionForwardBox from "~/components/QuestionForwardBox.vue";
import NotFoundPersonList from "~/components/people/NotFoundPersonList.vue";
import QuickOffer from "~/components/_refactored/app/boxes/QuickOffer.vue";
import PaginationComponent from "~/components/_refactored/common/PaginationComponent.vue";
import OfferBox from "~/components/people/OfferBox.vue";
import CityCountLinks from "~/components/mainpage/CityCountLinks.vue";
import BreadCrumbs from "~/components/_refactored/BreadCrumbs.vue";

import { generatePreloadLinks } from "~/helpers/files";
import { JOSZAKI_SEARCH_SET_CITY } from "~/plugins/joszaki";
import CardPerson from "~/components/people/CardPerson.vue";
import { useRequestTender } from "~/composables/requestTender";
import BlockTender from "~/components/_refactored/blocks/BlockTender.vue";
import MarkdownTextBlock from "~/components/_refactored/common/MarkdownTextBlock.vue";
import useRestClient from "~/helpers/restClient";
import { useUserStore } from "~/stores/user";
import { useNewTender } from "~/composables/tender/useNewTender";
import ProfessionTaskLinks from "~/components/people/ProfessionTaskLinks.vue";
import { useFetchProfessionals } from "~/components/_refactored/profession";
import errorCodes from "~/helpers/errorCodes";
import HierarchyHeader from "~/components/_refactored/ab/HierarchyHeader.vue";
import TableOfContents from "~/components/_refactored/app/boxes/TableOfContents.vue";

export default {
  name: "ProfessionalsPage",
  components: {
    ProfessionTaskLinks,
    MarkdownTextBlock,
    BlockTender,
    PaginationComponent,
    NotFoundPersonList,
    QuickOffer,
    QuestionInsetBox,
    QuestionForwardBox,
    OfferBox,
    CityCountLinks,
    BreadCrumbs,
    NotFound: () => import("~/components/listpage/NotFound.vue"),
    CardPerson,
    HierarchyHeader,
    TableOfContents,
  },
  layout: "DefaultLayout",
  setup() {
    const newTender = useNewTender();
    newTender.showFloater();

    const fetchProfessionals = useFetchProfessionals();
    return {
      userStore: useUserStore(),
      ...useRequestTender(),
      fetchProfessionals,
    };
  },
  async asyncData(context) {
    const pageParam = context.route.query.page
      ? parseInt(context.route.query.page)
      : null;
    if (isNaN(pageParam) || (Number.isInteger(pageParam) && pageParam <= 1)) {
      const newQuery = { ...context.route.query };
      delete newQuery.page;

      return context.redirect(301, {
        ...context.route,
        query: newQuery,
      });
    }

    const restClient = useRestClient(context.$api);

    const fetchProfessionals = useFetchProfessionals(context);

    const professionSeoName = context.route.params.profession;
    const citySeoName = context.route.params.city;
    const requestedPage = pageParam ?? 1;

    try {
      const [city, county, profession, blocksResponse] = await Promise.all([
        restClient.cities.bySeoName(citySeoName),
        restClient.counties.bySeoName(citySeoName),
        restClient.professions.bySeoName(professionSeoName),
        context.$query(PROFESSION_BLOCKS, {
          professionSeoName,
        }),
      ]);

      const blocks = blocksResponse.professionBySeoName.blocks.map((block) => ({
        id: block.id,
        type: block.type,
        value: {
          title: block.value.title.replaceAll("{cityName}", city.name),
          markdown: block.value.markdown.replaceAll("{cityName}", city.name),
        },
      }));

      const limit = context.$device.isMobile ? 20 : 50;

      if ((!city && !county) || !profession) {
        context.error({
          statusCode: 404,
          message: context.i18n.t("error.codes.ENTITY_NOT_FOUND"),
        });
      }

      if (city) {
        const [professionals, questions, popularProfessionTasks] =
          await Promise.all([
            fetchProfessionals({
              page: requestedPage,
              pageSize: limit,
              professionSeoName,
              citySeoName,
            }),
            restClient.questions.get({
              professionSeoName,
              minAnswerCount: 3,
              page: 1,
              size: 2,
              order: "desc",
              orderBy: "created",
            }),
            restClient.professionTasks.popularByProfessionSeoName({
              professionSeoName,
              citySeoName,
            }),
          ]);

        await Promise.all([
          context.store.dispatch("listPage/setCity", city),
          context.store.dispatch("listPage/setProfession", profession),
          context.store.dispatch("listPage/setProfessionTask", null),
          context.store.dispatch("listPage/setCounty", null),
        ]);

        return {
          limit,
          city,
          county: null,
          profession,
          totals: {
            averageScore: professionals?.peopleScores?.averageScore ?? 0,
            totalScoreCount: professionals?.peopleScores?.totalScoreCount ?? 0,
          },
          personList: professionals?.people ?? [],
          cityPersonCounts: [],
          paginationInfo: professionals.pagination,
          questions,
          popularProfessionTasks,
          blocks,
        };
      } else if (county) {
        const cityPersonCounts = await restClient.cities.get({
          countySeoName: citySeoName,
          order: "desc",
          orderBy: "population",
        });

        await Promise.all([
          context.store.dispatch("listPage/setCity", null),
          context.store.dispatch("listPage/setProfession", profession),
          context.store.dispatch("listPage/setProfessionTask", null),
          context.store.dispatch("listPage/setCounty", county),
        ]);

        return {
          city: null,
          county,
          profession,
          totals: {
            averageScore: 0.0,
            totalScoreCount: 0,
          },
          personList: [],
          cityPersonCounts,
          paginationInfo: null,
          questions: [],
          popularProfessionTasks: [],
        };
      }
    } catch (error) {
      context.$logger.error(
        errorCodes.PEOPLE_BY_CITY_AND_PROFESSION_ASYNC_DATA_ERROR,
        error
      );
      context.error({
        statusCode: 500,
        message: context.i18n.t("error.codes.INTERNAL_SERVER_ERROR"),
      });
    }
    return {
      limit: 20,
      city: undefined,
      profession: undefined,
      totals: {
        averageScore: 0.0,
        totalScoreCount: 0,
      },
      personList: [],
      paginationInfo: null,
    };
  },
  data() {
    return {
      loading: false,
    };
  },
  head() {
    if (this.profession && this.city) {
      const nthPage =
        this.paginationInfo?.current > 1
          ? this.$t("seo.professionalsCityPage.pageNumber", {
              pageNumber: this.paginationInfo?.current,
            })
          : "";
      const title = this.$t("seo.professionalsCityPage.title", {
        professionPlural: this.profession.plural,
        cityName: this.city.name,
        pageNumber: nthPage,
      });
      const decription = this.$t("seo.professionalsCityPage.description", {
        personCount: this.paginationInfo?.total ?? 0,
        cityName: this.city.name.toLowerCase(),
        professionTheperson: this.profession.theperson,
      });
      return {
        title,
        meta: [
          {
            hid: "og:title",
            property: "og:title",
            content: title,
          },
          {
            hid: "og:url",
            property: "og:url",
            content: `${this.$helpers.topDomain}${this.$route.fullPath}`,
          },
          {
            hid: "og:image",
            property: "og:image",
            content: "/images/common/szaki.png",
          },
          {
            hid: "description",
            name: "description",
            content: decription,
          },
          {
            hid: "og:description",
            property: "og:description",
            content: decription,
          },
        ],
        link: [
          {
            rel: "canonical",
            href: `${this.$helpers.topDomain}${this.$route.path}${
              this.paginationInfo?.current > 1
                ? "?page=" + this.paginationInfo.current
                : ""
            }`,
          },
        ].concat(
          generatePreloadLinks([
            NunitoRegular,
            NunitoSemibold,
            NunitoBold,
            NunitoExtrabold,
          ])
        ),
      };
    }
    if (this.profession && this.county) {
      const title = this.$t("seo.professionalsCountyPage.title", {
        professionPlural: this.profession.plural,
        countyName: this.county.name,
      });
      const description = this.$t("seo.professionalsCountyPage.description");
      return {
        title,
        meta: [
          {
            hid: "og:title",
            property: "og:title",
            content: title,
          },
          {
            hid: "description",
            name: "description",
            content: description,
          },
          {
            hid: "og:url",
            property: "og:url",
            content: `${this.$helpers.topDomain}${this.$route.fullPath}`,
          },
          {
            hid: "og:description",
            property: "og:description",
            content: description,
          },
          {
            hid: "og:image",
            property: "og:image",
            content: "/images/common/szaki.png",
          },
        ],
        link: generatePreloadLinks([NunitoRegular, NunitoBold]),
      };
    }
    return {
      title: this.$t("seo.professionalsCityPage.notFoundTitle"),
      meta: [
        {
          name: "robots",
          content: "noindex",
        },
      ],
      link: generatePreloadLinks([
        NunitoRegular,
        NunitoSemibold,
        NunitoBold,
        NunitoExtrabold,
      ]),
    };
  },
  computed: {
    abHierarchyHeader() {
      return this.$ab.isVariant("listpage-visual-hierarchy-header", "test");
    },
    extraText() {
      const filteredTexts = (PROFESSION_EXTRA_TEXTS ?? []).filter(
        (text) =>
          text.citySeoName === this.city.seoName &&
          text.professionSeoName === this.profession.seoName
      );

      if (filteredTexts.length > 0) {
        return filteredTexts[0].text;
      }
      return null;
    },
    tableOfContentsData() {
      return (this.blocks ?? []).map((block) => ({
        title: block.value.title,
      }));
    },
    quickOfferPlaceholder() {
      if (!this.profession) {
        return null;
      }

      const professionsWithFixSpecificPlaceholder = [
        "acs",
        "badogos",
        "haztartasi-gep-szerviz",
      ];
      const shouldUseProfessionSpecificPlaceholder =
        professionsWithFixSpecificPlaceholder.includes(this.profession.seoName);
      if (shouldUseProfessionSpecificPlaceholder) {
        return this.$t(`quickOfferPlaceholder.${this.profession.seoName}`);
      }

      return null;
    },
    getBreadCrumbs() {
      const links = [];

      if (this.profession?.name) {
        if (this.profession.professionGroup) {
          const professionsPagePath = this.localePath({
            name: "professionsPage",
            params: {
              professionGroup: this.profession.professionGroup.seoName,
            },
          });
          links.push({
            name: this.profession.professionGroup.name,
            page: professionsPagePath,
          });
        }
        links.push({
          name: this.profession.plural,
          page: this.localePath({
            name: "professionPage",
            params: { profession: this.profession.seoName },
          }),
        });
      }
      if (this.city?.name && this.profession?.name) {
        links.push({
          name: this.$t("listPage.breadCrumb.page", {
            city: this.city.name,
            professionPlural: this.profession.plural.toLowerCase(),
          }),
          link: "",
        });
      }

      return links;
    },
    isAdmin() {
      return this.userStore.isAdmin;
    },
    isDev() {
      return this.$helpers.isDev();
    },
    totalScore() {
      return this.totals.averageScore / 2;
    },
    found() {
      return this.profession && this.city && this.personList.length;
    },
    professionName() {
      if (!this.profession || !this.profession.theperson) {
        return "";
      }
      return this.profession.theperson;
    },
    moreThan10Active() {
      if (!this.personList) {
        return false;
      }
      return this.personList.filter((x) => x.isActive).length > 10;
    },
    peopleIds() {
      return this.personList.map((x) => {
        return x.id;
      });
    },
  },
  watch: {
    $route: {
      handler() {
        this.$refs.header.scrollIntoView();
        this.loadProfessionalsPage(this.$route.query.page ?? 1);
      },
    },
  },
  beforeMount() {
    this.trackViewItemList();
  },
  mounted() {
    // this.$nuxt.$on('listpage-filter-changed', this.filtersChanged)
    this.trackPageView();

    this.seoTrackCurrentPage();
    this.$mitt.on(JOSZAKI_SEARCH_SET_CITY, this.changeCity);
  },
  beforeDestroy() {
    // this.$nuxt.$off('listpage-filter-changed', this.filtersChanged)
    this.trackPageLeave();
    this.$mitt.off(JOSZAKI_SEARCH_SET_CITY, this.changeCity);
  },
  methods: {
    changeCity(city) {
      if (!city.seoName) {
        return;
      }
      const path = this.localePath({
        name: "professionals",
        params: { city: city.seoName, profession: this.profession.seoName },
      });
      this.$router.push(path);
    },
    trackPageView() {
      if (this.profession && this.city) {
        this.$trackers.pageview({
          pagename: this.$trackers.pageNames.PROFESSION_CITY_PAGE,
          pagenumber: this.paginationInfo?.current,
          professionSeoName: this.profession?.seoName,
          citySeoName: this.city?.seoName,
          professionGroupSeoName: this.profession?.professionGroup?.seoName,
        });
      } else if (this.profession && this.county) {
        this.$trackers.pageview({
          pagename: this.$trackers.pageNames.PROFESSION_COUNTY_PAGE,
          pagenumber: this.paginationInfo?.current,
          professionSeoName: this.profession?.seoName,
          countySeoName: this.county?.seoName,
          professionGroupSeoName: this.profession?.professionGroup?.seoName,
        });
      }
    },
    trackPageLeave() {
      if (this.profession && this.city) {
        this.$trackers.pageleave({
          pagename: this.$trackers.pageNames.PROFESSION_CITY_PAGE,
          pagenumber: this.paginationInfo?.current,
        });
      } else if (this.profession && this.county) {
        this.$trackers.pageleave({
          pagename: this.$trackers.pageNames.PROFESSION_COUNTY_PAGE,
          pagenumber: this.paginationInfo?.current,
        });
      }
    },
    async loadProfessionalsPage(requestedPage) {
      this.loading = true;
      try {
        // don't track leave if we are applying filters
        if (requestedPage || parseInt(this.$route.query.page) > 1) {
          this.trackPageLeave();
        }

        const resp = await this.fetchProfessionals({
          professionSeoName: this.profession.seoName,
          citySeoName: this.city.seoName,
          pageSize: this.limit,
          page: requestedPage ? parseInt(requestedPage) : null,
        });
        this.paginationInfo = resp.pagination;
        this.personList = resp.people;
        // don't track view if we are applying filters
        if (requestedPage) {
          this.trackViewItemList();
          this.seoTrackCurrentPage();
          this.trackPageView();
        }
      } catch (error) {
        console.error("Failed to load more people", error);
        this.$sentry.captureException(error);
        this.$sentry.captureException(JSON.stringify(error));
      }
      this.loading = false;
    },
    onCallProfessional(person) {
      this.trackOpenPersonProfile(person);

      this.$trackers.trackEvent({
        title: "Clicked On Mate Phone Number",
        data: {
          personId: person.id,
          professionSeoName: this.profession.seoName,
          citySeoName: this.city.seoName,
          countySeoName: this.county?.seoName,
          professionGroupSeoName: this.profession?.professionGroup?.seoName,
        },
      });

      // this.$track.customEvent({
      //   event: 'initiate_call',
      //   city_id: this.city.id,
      //   profession_id: this.profession.id,
      //   ecommerce: { items: [person.id] },
      // })

      const path = this.localePath({
        name: "professionalPage",
        params: {
          professionalSeoName: person.seoName,
        },
        query: {
          city: this.city?.seoName,
          profession: this.profession?.seoName,
        },
      });
      window.open(path);
    },
    onRequestTender({ person, source, text }) {
      this.requestTender({
        person,
        profession: this.profession,
        city: this.city,
        source,
        description: text,
      });
    },
    onClickPersonCard(person) {
      this.trackOpenPersonProfile(person);

      // this.$trackers.trackEvent({
      //   title: "Clicked On Mate Card",
      //   data: {
      //     personId: person.id,
      //     professionSeoName: this.profession.seoName,
      //     citySeoName: this.city.seoName,
      //     countySeoName: this.county?.seoName,
      //     professionGroupSeoName: this.profession?.professionGroup?.seoName,
      //   },
      // });

      const path = this.localePath({
        name: "professionalPage",
        params: {
          professionalSeoName: person.seoName,
        },
        query: {
          city: this.city?.seoName,
          profession: this.profession?.seoName,
        },
      });
      window.open(path);
    },
    seoTrackCurrentPage() {
      if (!this.personLis || !this.city) {
        return;
      }
      let activeProfiles = 0;
      let inactiveProfiles = 0;
      let profilesWithReviews = 0;
      let profilesWith10Reviews = 0;
      let profilesWith100Reviews = 0;
      let profilesWithOrders = 0;
      let profilesWith10Orders = 0;
      let profilesWith100Orders = 0;
      for (const person of this.personList) {
        person.isActive ? activeProfiles++ : inactiveProfiles++;
        if (person.winCount >= 100) {
          profilesWith100Orders++;
        } else if (person.winCount >= 10) {
          profilesWith10Orders++;
        } else if (person.winCount > 0) {
          profilesWithOrders++;
        }
        if (person.scoreCount >= 100) {
          profilesWith100Reviews++;
        } else if (person.scoreCount >= 10) {
          profilesWith10Reviews++;
        } else if (person.scoreCount > 0) {
          profilesWithReviews++;
        }
      }

      this.$track.customEvent({
        event: "professionCityListPage",
        profession: this.profession.seoName,
        location: this.city.seoName,
        profiles: this.personList.length, // Hány szaki van összesen?
        // localProfiles: '50', // Hány szaki van 10 kilométeres körzetben?
        activeProfiles, // Hány szaki aktív?
        inactiveProfiles, // Hány szaki inaktív?
        profilesWithReviews, // Hány szaki van legalább 1 értékeléssel?
        profilesWith10Reviews, // Hány szaki van legalább 10 értékeléssel?
        profilesWith100Reviews, // Hány szaki van legalább 100 értékeléssel?
        profilesWithOrders, // Hány szaki van legalább 1 megbízással?
        profilesWith10Orders, // Hány szaki van legalább 10 megbízással?
        profilesWith100Orders, // Hány szaki van legalább 100 megbízással?
      });
    },

    trackViewItemList() {
      this.$track.customEvent({
        event: "view_item_list",
        source_page: "nuxt_list",
        city_id: this.city ? this.city.id : null,
        profession_id: this.profession,
        item_list_name: this.profession.name, // TODO : What?
        item_list_id: this.profession.id, // TODO : What?
        ecommerce: {
          items: this.peopleIds.map((id) => {
            const people = this.personList.find((x) => x.id === id);
            const offset = ((this.$route.query.page ?? 1) - 1) * this.limit;
            return {
              index: this.peopleIds.indexOf(id) + offset,
              item_id: people.id,
              item_name: people.name,
              item_list_name: `${this.profession.name} ${this.city.name}`,
              item_list_id: `${this.city.seoName}#${this.profession.seoName}`,
              item_category: this.profession.name,
              item_category2: this.city.name,
            };
          }),
        },
      });
    },
    trackOpenPersonProfile(person) {
      this.$track.customEvent({ ecommerce: null });
      const offset = ((this.$route.query.page ?? 1) - 1) * this.limit;
      this.$track.customEvent({
        event: "select_item",
        city_id: this.city.id,
        profession_id: this.profession.id,
        ecommerce: {
          items: [
            {
              index: this.peopleIds.indexOf(person.id) + offset,
              item_id: person.id,
              item_name: person.name,
              item_list_name: `${this.profession?.name} ${this.city?.name}`,
              item_list_id: `${this.city?.seoName}#${this.profession?.seoName}`,
            },
          ],
        },
        /* { */
        /*   index: this.peopleIds.indexOf(person.id) + offset, */
        /*   item_id: person.id, */
        /*   item_name: person.name, */
        /*   item_list_name: `${this.profession.name} ${this.city.name}`, */
        /*   item_list_id: `${this.city.seoName}#${this.profession.seoName}`, */
        /*   item_category: this.profession.name, */
        /*   item_category2: this.city.name */
        /* } */
        /* ] */
        /* } */
      });
    },
  },
};
</script>

<style>
.autocomplete .dropdown-item {
  padding-right: 1rem;
  font-size: 0.7rem;
}
</style>

<style lang="scss" scoped>
@use "styles/_all-members" as global;

.loading {
  cursor: progress !important;

  * {
    cursor: progress !important;
  }
}

.profession-tasks-heading {
  @include global.mobile {
    padding: 0 10px;
  }
}

.listpage-header {
  display: grid;
  gap: 10px;
  padding: 10px 0;

  .page-title {
    margin: 20px auto 15px;
    text-align: center;
    font-size: 36px;
    font-weight: 800;
  }
}

.split-listpage-header {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;
  padding: 10px 0px;
  margin-top: 20px;
  isolation: isolate;
  position: relative;
  $hue-rotate: 0deg;

  @include global.touch {
    gap: 20px;
    grid-template-columns: 1fr;
  }

  @include global.mobile {
    margin-bottom: 0;
  }

  @include global.tablet {
    margin-bottom: 10px;
  }

  @include global.desktop {
    margin-bottom: 20px;
  }

  .quick-offer {
    filter: hue-rotate($hue-rotate);
  }

  .intro-text {
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: relative;

    @include global.touch {
      align-items: center;
      text-align: center;
    }

    .title {
      font-weight: 800;
      font-size: 38px;
      color: global.$primary;
      filter: hue-rotate($hue-rotate);

      @include global.mobile {
        font-size: 20px;
      }
    }

    .description {
      font-size: 24px;
      font-weight: 600;

      @include global.mobile {
        font-size: 14px;
      }
    }
  }

  .head-logo {
    position: absolute;
    left: 0%;
    top: 50%;
    transform: translateX(-50%) translateY(-50%);
    width: 240px;
    height: 240px;
    opacity: 0.05;
    z-index: -1;
  }
}

.split-listpage-info {
  display: block;

  .person-count {
    text-align: center;
    font-size: 14px;
    font-weight: 700;
  }
}

.listpage-list-wrapper {
  display: grid;
  gap: 10px;
}

.load-more {
  margin-top: 10px;
  height: 5.5rem;
  display: flex;
  justify-content: center;
}

.rating-summary {
  margin-bottom: 15px;
}

.dynamic-text {
  word-break: break-word;
}
</style>

<style lang="scss">
@use "styles/_all-members" as global;
</style>
